<!-- views/admin/LoginPage.vue -->
<template>
  <div class="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10 flex items-center justify-center p-4">
    <!-- Toast Notification -->
    <Transition
      enter-active-class="transform transition duration-300 ease-out"
      enter-from-class="translate-y-2 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transform transition duration-200 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-2 opacity-0"
    >
      <div
        v-if="toastMessage"
        :class="[
          'fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2',
          error ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground'
        ]"
      >
        <component :is="error ? AlertCircle : CheckCircle" class="h-5 w-5" />
        {{ toastMessage }}
      </div>
    </Transition>

    <!-- Animated Background -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute -top-1/2 -right-1/2 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
      <div class="absolute -bottom-1/2 -left-1/2 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse delay-700"></div>
    </div>

    <!-- Login Card -->
    <div class="w-full max-w-md relative animate-fade-in-up">
      <!-- Logo & Brand -->
      <div class="text-center mb-8">
        <div class="inline-block p-4 bg-primary text-primary-foreground rounded-2xl shadow-lg mb-4 animate-bounce-slow">
          <Lock class="h-12 w-12" />
        </div>
        <h1 class="text-3xl font-bold gradient-text">Espace Admin</h1>
        <p class="text-muted-foreground mt-2">Connectez-vous pour accéder au tableau de bord</p>
      </div>

      <!-- Card -->
      <div class="bg-card text-card-foreground rounded-2xl border border-border shadow-2xl overflow-hidden backdrop-blur-sm bg-card/95">
        <!-- Tabs -->
        <div class="flex border-b border-border">
          <button
            @click="activeTab = 'login'"
            class="flex-1 px-6 py-4 text-sm font-medium transition-all relative"
            :class="activeTab === 'login' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'"
          >
            Connexion
            <span
              v-if="activeTab === 'login'"
              class="absolute bottom-0 left-0 w-full h-0.5 bg-primary"
            ></span>
          </button>
          <button
            @click="activeTab = 'forgot'"
            class="flex-1 px-6 py-4 text-sm font-medium transition-all relative"
            :class="activeTab === 'forgot' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'"
          >
            Mot de passe oublié
            <span
              v-if="activeTab === 'forgot'"
              class="absolute bottom-0 left-0 w-full h-0.5 bg-primary"
            ></span>
          </button>
        </div>

        <!-- Login Form -->
        <div v-if="activeTab === 'login'" class="p-8">
          <form @submit.prevent="handleLogin" class="space-y-6">
            <!-- Email Field -->
            <div class="space-y-2">
              <label for="email" class="text-sm font-medium flex items-center gap-1">
                <Mail class="h-4 w-4" />
                Adresse email
              </label>
              <div class="relative">
                <input
                  id="email"
                  v-model="loginForm.email"
                  type="email"
                  required
                  placeholder="admin@exemple.com"
                  class="input-base pl-10"
                  :class="{ 'border-destructive ring-destructive': errors.email }"
                />
                <Mail class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              </div>
              <p v-if="errors.email" class="text-sm text-destructive">{{ errors.email }}</p>
            </div>

            <!-- Password Field -->
            <div class="space-y-2">
              <label for="password" class="text-sm font-medium flex items-center gap-1">
                <Key class="h-4 w-4" />
                Mot de passe
              </label>
              <div class="relative">
                <input
                  :id="showPassword ? 'text' : 'password'"
                  v-model="loginForm.password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  placeholder="••••••••"
                  class="input-base pl-10 pr-10"
                  :class="{ 'border-destructive ring-destructive': errors.password }"
                />
                <Key class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <component :is="showPassword ? EyeOff : Eye" class="h-4 w-4" />
                </button>
              </div>
              <p v-if="errors.password" class="text-sm text-destructive">{{ errors.password }}</p>
            </div>

            <!-- Remember Me & Forgot -->
            <div class="flex items-center justify-between">
              <label class="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  v-model="loginForm.remember"
                  class="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <span class="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  Se souvenir de moi
                </span>
              </label>
              <button
                type="button"
                @click="activeTab = 'forgot'"
                class="text-sm text-primary hover:text-primary/80 transition-colors"
              >
                Mot de passe oublié ?
              </button>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              class="btn-primary w-full py-3 gradient-primary relative overflow-hidden group"
              :disabled="loading"
            >
              <span class="relative z-10 flex items-center justify-center gap-2">
                <span v-if="loading" class="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></span>
                <LogIn v-else class="h-5 w-5" />
                {{ loading ? 'Connexion en cours...' : 'Se connecter' }}
              </span>
              <span class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></span>
            </button>
          </form>

          <!-- Security Notice -->
          <div class="mt-6 pt-6 border-t border-border">
            <p class="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
              <Shield class="h-4 w-4" />
              Connexion sécurisée • Accès réservé aux administrateurs
            </p>
          </div>
        </div>

        <!-- Forgot Password Form -->
        <div v-else class="p-8">
          <div class="text-center mb-6">
            <KeyRound class="h-12 w-12 mx-auto mb-4 text-primary" />
            <h2 class="text-xl font-bold">Réinitialiser le mot de passe</h2>
            <p class="text-sm text-muted-foreground mt-2">
              Entrez votre email pour recevoir un lien de réinitialisation
            </p>
          </div>

          <form @submit.prevent="handleForgotPassword" class="space-y-6">
            <div class="space-y-2">
              <label for="reset-email" class="text-sm font-medium">Adresse email</label>
              <input
                id="reset-email"
                v-model="forgotForm.email"
                type="email"
                required
                placeholder="admin@exemple.com"
                class="input-base"
              />
            </div>

            <button
              type="submit"
              class="btn-primary w-full"
              :disabled="loading"
            >
              {{ loading ? 'Envoi en cours...' : 'Envoyer le lien' }}
            </button>

            <button
              type="button"
              @click="activeTab = 'login'"
              class="text-sm text-primary hover:text-primary/80 w-full text-center"
            >
              ← Retour à la connexion
            </button>
          </form>
        </div>
      </div>

      <!-- Footer -->
      <p class="text-center text-sm text-muted-foreground mt-8">
        &copy; {{ new Date().getFullYear() }} Votre Organisation. Tous droits réservés.
      </p>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import {
  Lock,
  Mail,
  Key,
  Eye,
  EyeOff,
  LogIn,
  Shield,
  KeyRound,
  CheckCircle,
  AlertCircle
} from 'lucide-vue-next'

export default {
  name: 'AdminLoginPage',

  components: {
    Lock,
    Mail,
    Key,
    Eye,
    EyeOff,
    LogIn,
    Shield,
    KeyRound,
    CheckCircle,
    AlertCircle
  },

  data() {
    return {
      activeTab: 'login',
      showPassword: false,
      loginForm: {
        email: '',
        password: '',
        remember: false
      },
      forgotForm: {
        email: ''
      },
      errors: {
        email: '',
        password: ''
      }
    }
  },

  computed: {
    ...mapState({
      loading: state => state.loading,
      error: state => state.error,
      toastMessage: state => state.toastMessage,
      isAuthenticated: state => !!state.token
    })
  },

  watch: {
    isAuthenticated: {
      handler(value) {
        if (value) {
          // Rediriger vers le dashboard après connexion réussie
          setTimeout(() => {
            this.$router.push('/systeme/admin/dashboard')
          }, 1500)
        }
      },
      immediate: true
    },

    // Réinitialiser les erreurs quand on change d'onglet
    activeTab() {
      this.clearErrors()
    }
  },

  methods: {
    ...mapActions(['login', 'logout']),

    validateForm() {
      this.clearErrors()
      let isValid = true

      if (!this.loginForm.email) {
        this.errors.email = "L'email est requis"
        isValid = false
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.loginForm.email)) {
        this.errors.email = "Format d'email invalide"
        isValid = false
      }

      if (!this.loginForm.password) {
        this.errors.password = 'Le mot de passe est requis'
        isValid = false
      } else if (this.loginForm.password.length < 6) {
        this.errors.password = 'Le mot de passe doit contenir au moins 6 caractères'
        isValid = false
      }

      return isValid
    },

    clearErrors() {
      this.errors = {
        email: '',
        password: ''
      }
    },

    async handleLogin() {
      if (!this.validateForm()) return

      try {
        await this.login(this.loginForm)
        // La redirection est gérée par le watcher
      } catch (error) {
        console.error('Login error:', error)
        // Les erreurs sont déjà gérées dans le store
      }
    },

    async handleForgotPassword() {
      if (!this.forgotForm.email) return

      try {
        // Implémentez votre logique de réinitialisation de mot de passe ici
        // Cette fonction devrait être ajoutée à votre store
        console.log('Reset password for:', this.forgotForm.email)
        
        // Simulation d'envoi
        this.$store.commit('SET_TOAST', 'Email de réinitialisation envoyé !')
        
        // Retour à la connexion après 2 secondes
        setTimeout(() => {
          this.activeTab = 'login'
          this.forgotForm.email = ''
        }, 2000)
      } catch (error) {
        console.error('Forgot password error:', error)
      }
    },

    async handleLogout() {
      await this.logout()
    }
  }
}
</script>

<style scoped>
/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes bounceSlow {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out;
}

.animate-bounce-slow {
  animation: bounceSlow 2s ease-in-out infinite;
}

.delay-1000 {
  animation-delay: 1s;
}

.delay-700 {
  animation-delay: 0.7s;
}

/* Gradient Text */
.gradient-text {
  background: linear-gradient(135deg, hsl(183, 100%, 25%) 0%, hsl(183, 100%, 40%) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Button Styles */
.btn-primary {
  @apply bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium transition-all inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-primary:not(:disabled):hover {
  @apply bg-primary/90 transform scale-[1.02];
}

.btn-primary:not(:disabled):active {
  @apply transform scale-[0.98];
}

/* Input Styles */
.input-base {
  @apply flex h-12 w-full rounded-lg border border-input bg-background  py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all;
}

.input-base:focus {
  @apply border-primary;
}

/* Gradient Background */
.gradient-primary {
  background: linear-gradient(135deg, hsl(183, 100%, 17%) 0%, hsl(183, 100%, 30%) 100%);
}

/* Backdrop Blur */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
}

/* Card Shadow */
.shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Responsive */
@media (max-width: 640px) {
  .p-8 {
    padding: 1.5rem;
  }
}
</style>